import { useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Card, CardContent } from '@/components/ui/card'
import { RefreshCw, AlertTriangle, Search } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { toast } from 'sonner'
import {
  useInstalledOn,
  useSyncSite,
  useSyncPlugin,
  type InstalledSite,
} from '../hooks/usePluginInventory'

interface InstalledOnTabProps {
  slug: string
  pluginName: string
}

type StatusFilter = 'all' | 'active' | 'inactive' | 'must-use'

export function InstalledOnTab({ slug, pluginName }: InstalledOnTabProps) {
  const { data, isLoading, error } = useInstalledOn(slug)
  const syncSite = useSyncSite()
  const syncPlugin = useSyncPlugin()

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [versionFilter, setVersionFilter] = useState<string | null>(null)

  const sortedVersions = useMemo(() => {
    if (!data?.versions) return []
    return Object.entries(data.versions).sort((a, b) => b[1] - a[1])
  }, [data?.versions])

  const filteredSites = useMemo<InstalledSite[]>(() => {
    if (!data?.sites) return []
    return data.sites.filter(s => {
      if (search && !s.domain.toLowerCase().includes(search.toLowerCase())) return false
      if (statusFilter !== 'all' && s.status !== statusFilter) return false
      if (versionFilter && s.version !== versionFilter) return false
      return true
    })
  }, [data?.sites, search, statusFilter, versionFilter])

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm">
        Failed to load installation data. {error instanceof Error ? error.message : ''}
      </div>
    )
  }

  if (!data || data.site_count === 0) {
    return (
      <div className="rounded-md border bg-muted/30 p-8 text-center">
        <p className="text-sm text-muted-foreground mb-4">
          No sites have {pluginName} recorded yet. Plugin inventory syncs from Rocket.net
          on a schedule — once the sync runs you'll see which sites have it installed.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            syncPlugin.mutate(slug, {
              onSuccess: (res) => {
                if (res.sitesProcessed === 0) {
                  toast.info('No sites currently recorded with this plugin. Try again after a scheduled sync has run.')
                } else {
                  toast.success(`Synced ${res.sitesSucceeded} of ${res.sitesProcessed} sites`)
                }
              },
              onError: (err) => toast.error(`Sync failed: ${err instanceof Error ? err.message : 'unknown'}`),
            })
          }}
          disabled={syncPlugin.isPending}
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${syncPlugin.isPending ? 'animate-spin' : ''}`} />
          Try syncing now
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Summary strip */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <div className="text-2xl font-bold">{data.site_count}</div>
              <div className="text-xs text-muted-foreground">
                Installed on sites ({data.active_count} active)
              </div>
            </div>
            <div>
              <div className="text-2xl font-bold">{sortedVersions.length}</div>
              <div className="text-xs text-muted-foreground">
                Distinct versions in the fleet
              </div>
            </div>
            <div>
              <div className="text-2xl font-bold flex items-center gap-2">
                {data.update_available_count}
                {data.update_available_count > 0 && (
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                )}
              </div>
              <div className="text-xs text-muted-foreground">
                Sites with updates available
              </div>
            </div>
          </div>

          {/* Version breakdown */}
          {sortedVersions.length > 0 && (
            <div className="mt-6">
              <div className="text-xs text-muted-foreground mb-2">Version breakdown — click to filter</div>
              <div className="flex flex-wrap gap-2">
                {sortedVersions.map(([version, count]) => (
                  <Badge
                    key={version}
                    variant={versionFilter === version ? 'default' : 'secondary'}
                    className="cursor-pointer"
                    onClick={() => setVersionFilter(versionFilter === version ? null : version)}
                  >
                    v{version}: {count}
                  </Badge>
                ))}
                {versionFilter && (
                  <Button variant="ghost" size="sm" onClick={() => setVersionFilter(null)}>
                    Clear filter
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Sync all button */}
          <div className="mt-6 flex items-center justify-between pt-4 border-t">
            <div className="text-xs text-muted-foreground">
              Data refreshes from Rocket.net on a schedule. For a security response — force-sync every site with this plugin now.
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                syncPlugin.mutate(slug, {
                  onSuccess: (res) => {
                    toast.success(
                      `Synced ${res.sitesSucceeded} of ${res.sitesProcessed} sites` +
                      (res.sitesFailed > 0 ? ` (${res.sitesFailed} failed)` : '')
                    )
                  },
                  onError: (err) => toast.error(`Sync failed: ${err instanceof Error ? err.message : 'unknown'}`),
                })
              }}
              disabled={syncPlugin.isPending}
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${syncPlugin.isPending ? 'animate-spin' : ''}`} />
              Sync all sites
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Filter by domain…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>
        <div className="flex gap-1">
          {(['all', 'active', 'inactive', 'must-use'] as StatusFilter[]).map(s => (
            <Button
              key={s}
              variant={statusFilter === s ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter(s)}
            >
              {s === 'all' ? 'All' : s}
            </Button>
          ))}
        </div>
      </div>

      {/* Sites table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Domain</TableHead>
              <TableHead>Version</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last synced</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredSites.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground h-24">
                  No sites match the current filters.
                </TableCell>
              </TableRow>
            ) : (
              filteredSites.map(site => (
                <TableRow key={site.site_id}>
                  <TableCell className="font-mono text-sm">
                    <a
                      href={`https://${site.domain}`}
                      target="_blank"
                      rel="noreferrer"
                      className="hover:underline"
                    >
                      {site.domain}
                    </a>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm">{site.version}</span>
                      {site.update_available && (
                        <Badge variant="outline" className="text-amber-700 border-amber-300">
                          update
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={site.status} />
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatSyncedAt(site.synced_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        syncSite.mutate(site.site_id, {
                          onSuccess: (res) => {
                            if (res.success) toast.success(`Synced ${site.domain} (${res.pluginCount} plugins)`)
                            else toast.error(`Sync failed: ${res.error ?? 'unknown'}`)
                          },
                          onError: (err) => toast.error(`Sync failed: ${err instanceof Error ? err.message : 'unknown'}`),
                        })
                      }}
                      disabled={syncSite.isPending && syncSite.variables === site.site_id}
                    >
                      <RefreshCw
                        className={`h-4 w-4 ${syncSite.isPending && syncSite.variables === site.site_id ? 'animate-spin' : ''}`}
                      />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'active') return <Badge variant="default">active</Badge>
  if (status === 'inactive') return <Badge variant="secondary">inactive</Badge>
  if (status === 'must-use') return <Badge variant="outline">must-use</Badge>
  return <Badge variant="outline">{status}</Badge>
}

function formatSyncedAt(syncedAt: number | string): string {
  try {
    const date = typeof syncedAt === 'number'
      ? new Date(syncedAt * (syncedAt < 1e12 ? 1000 : 1))
      : new Date(syncedAt)
    if (Number.isNaN(date.getTime())) return '—'
    return `${formatDistanceToNow(date)} ago`
  } catch {
    return '—'
  }
}
